import { useEffect } from 'react';
import { Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from './auth/AuthProvider';
import { getStartParam } from './telegram';
import { useAppStatus } from './api/hooks';
import { useMaintenance, setMaintenance } from './api/maintenance';
import { MaintenanceScreen } from './screens/MaintenanceScreen';
import { initAnalytics, track } from './analytics';
import { Loader, ErrorView } from './components/StateViews';
import { TabBar } from './components/TabBar';
import { HomeScreen } from './screens/HomeScreen';
import { PassScreen } from './screens/PassScreen';
import { PassPlusScreen } from './screens/PassPlusScreen';
import { DecksScreen } from './screens/DecksScreen';
import { DeckOpenScreen } from './screens/DeckOpenScreen';
import { WardrobeScreen } from './screens/WardrobeScreen';
import { FreezesScreen } from './screens/FreezesScreen';
import { QuestScreen } from './screens/QuestScreen';
import { VouchersScreen } from './screens/VouchersScreen';
import { WardrobePreviewScreen } from './screens/WardrobePreviewScreen';
import { PlusCelebration } from './components/pass/PlusCelebration';
import { SeasonRecap } from './components/pass/SeasonRecap';
import { LeaderboardScreen } from './screens/LeaderboardScreen';
import { ScheduleScreen } from './screens/ScheduleScreen';
import { TournamentDetailScreen } from './screens/TournamentDetailScreen';
import { ProfileScreen } from './screens/ProfileScreen';
import { SettingsScreen } from './screens/SettingsScreen';
import { EditProfileScreen } from './screens/EditProfileScreen';
import { LoginScreen } from './screens/auth/LoginScreen';
import { TermsScreen } from './screens/auth/TermsScreen';
import { OnboardingScreen } from './screens/auth/OnboardingScreen';
import { RegisteredPlayersScreen } from './screens/RegisteredPlayersScreen';
import { MyTournamentsScreen } from './screens/MyTournamentsScreen';
import { TournamentResultScreen } from './screens/TournamentResultScreen';
import { LiveTournamentScreen } from './screens/LiveTournamentScreen';
import { PlayerProfileScreen } from './screens/PlayerProfileScreen';
import { HallOfFameScreen } from './screens/HallOfFameScreen';
import { SeasonChampionScreen } from './screens/SeasonChampionScreen';
import { AchievementsScreen } from './screens/AchievementsScreen';
import { AchievementCategoryScreen } from './screens/AchievementCategoryScreen';
import { AchievementDetailScreen } from './screens/AchievementDetailScreen';
import { CelebrationSheet } from './components/achievements/CelebrationSheet';
import { RatingLevelUpSheet } from './components/rating/RatingLevelUpSheet';
import { QualificationRecapSheet } from './components/qualification/QualificationRecapSheet';
import { IntroGate, IntroScreen } from './components/intro/IntroStories';
import { QualificationScreen } from './screens/QualificationScreen';
import { MyQualificationScreen } from './screens/MyQualificationScreen';
import { QualificationPlayerScreen } from './screens/QualificationPlayerScreen';
import { KaliningradMeScreen } from './screens/KaliningradMeScreen';
import { KaliningradRulesScreen } from './screens/KaliningradRulesScreen';
import { KaliningradRecap } from './components/qualification/KaliningradRecap';

function screenName(path: string): string {
  if (path === '/') return 'home';
  if (path === '/intro') return 'intro';
  if (/^\/pass\/decks\/\d+\/open/.test(path)) return 'pass_deck_open';
  if (path === '/pass/decks') return 'pass_decks';
  if (path === '/pass/wardrobe') return 'pass_wardrobe';
  if (path === '/pass/plus') return 'pass_plus';
  if (path === '/pass') return 'pass';
  if (path.startsWith('/leaderboard')) return 'leaderboard';
  if (path === '/qualification') return 'qualification';
  if (path === '/qualification/kaliningrad/me') return 'kaliningrad_me';
  if (path === '/qualification/kaliningrad/rules') return 'kaliningrad_rules';
  if (path === '/qualification/me') return 'qualification_me';
  if (path.startsWith('/qualification/players/')) return 'qualification_player';
  if (/^\/hall-of-fame\/\d+/.test(path)) return 'season_champion';
  if (path.startsWith('/hall-of-fame')) return 'hall_of_fame';
  if (/\/achievements\/categories\//.test(path)) return 'achievement_category';
  if (/\/achievements\/\d+/.test(path)) return 'achievement_detail';
  if (path.includes('/achievements')) return 'achievements';
  if (path.startsWith('/schedule')) return 'schedule';
  if (/^\/tournaments\/\d+\/players/.test(path)) return 'tournament_players';
  if (/^\/tournaments\/\d+\/results/.test(path)) return 'tournament_results';
  if (/^\/tournaments\/\d+\/live/.test(path)) return 'tournament_live';
  if (/^\/tournaments\/\d+/.test(path)) return 'tournament_detail';
  if (path.startsWith('/me/tournaments')) return 'my_tournaments';
  if (/^\/players\/\d+\/tournaments/.test(path)) return 'player_tournaments';
  if (path.startsWith('/players/')) return 'player_profile';
  if (path === '/profile') return 'profile';
  if (path.startsWith('/settings/edit')) return 'edit_profile';
  if (path.startsWith('/settings')) return 'settings';
  return path;
}

export function App() {
  const auth = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const maint = useMaintenance();
  // Poll /status only while paused (to detect recovery); otherwise just on load/focus.
  const status = useAppStatus(maint.on);

  useEffect(() => {
    initAnalytics();
  }, []);
  // Шаринг карточки чемпиона: ?startapp=hof_<seasonId> открывает её напрямую.
  // Одноразово на старте; для остальных payload'ов start_param остаётся
  // атрибуцией (analytics.ts) — их не роутим.
  useEffect(() => {
    const m = /^hof_(\d+)$/.exec(getStartParam() ?? '');
    if (m) navigate(`/hall-of-fame/${m[1]}`, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    track('screen_view', { screen: screenName(location.pathname), path: location.pathname });
  }, [location.pathname]);
  // /status is authoritative (on AND off): seeds on cold load, clears on recovery.
  useEffect(() => {
    if (status.data) setMaintenance(status.data.maintenance, status.data.message);
  }, [status.data]);

  // Maintenance mode overrides everything (incl. auth) — players see the stub.
  if (maint.on) return <MaintenanceScreen message={maint.message} />;

  if (auth.status === 'loading') return <Loader label="Загрузка…" />;
  if (auth.status === 'error') return <ErrorView message={auth.error} />;
  if (auth.status === 'login') return <LoginScreen />;
  if (auth.status === 'terms') return <TermsScreen />;
  if (auth.status === 'onboarding') return <OnboardingScreen />;

  const hideTabBar =
    /^\/tournaments\/\d+/.test(location.pathname) ||
    location.pathname.startsWith('/settings') ||
    location.pathname.startsWith('/me/') ||
    // Профиль игрока (R2-4) показывает таб-бар с активным «Рейтингом»;
    // его подэкраны (турниры, достижения) — без таб-бара, как раньше.
    /^\/players\/\d+\/.+/.test(location.pathname) ||
    location.pathname.startsWith('/hall-of-fame') ||
    // Полноэкранное открытие колоды (6.5) — без таб-бара.
    /^\/pass\/decks\/\d+\/open/.test(location.pathname) ||
    // Онбординг-сторис (повтор из настроек) — полноэкранный.
    location.pathname === '/intro';

  return (
    <div className="app">
      <Routes>
        <Route path="/" element={<HomeScreen />} />
        <Route path="/intro" element={<IntroScreen />} />
        <Route path="/leaderboard" element={<LeaderboardScreen />} />
        <Route path="/pass" element={<PassScreen />} />
        <Route path="/pass/plus" element={<PassPlusScreen />} />
        <Route path="/pass/decks" element={<DecksScreen />} />
        <Route path="/pass/decks/:id/open" element={<DeckOpenScreen />} />
        <Route path="/pass/quests/:id" element={<QuestScreen />} />
        <Route path="/pass/freezes" element={<FreezesScreen />} />
        <Route path="/pass/vouchers" element={<VouchersScreen />} />
        <Route path="/pass/wardrobe" element={<WardrobeScreen />} />
        <Route path="/pass/wardrobe/preview" element={<WardrobePreviewScreen />} />
        <Route path="/qualification" element={<QualificationScreen />} />
        <Route path="/qualification/kaliningrad/me" element={<KaliningradMeScreen />} />
        <Route path="/qualification/kaliningrad/rules" element={<KaliningradRulesScreen />} />
        <Route path="/qualification/me" element={<MyQualificationScreen />} />
        <Route path="/qualification/players/:id" element={<QualificationPlayerScreen />} />
        <Route path="/hall-of-fame" element={<HallOfFameScreen />} />
        <Route path="/hall-of-fame/:seasonId" element={<SeasonChampionScreen />} />
        <Route path="/achievements" element={<AchievementsScreen />} />
        <Route path="/achievements/categories/:catId" element={<AchievementCategoryScreen />} />
        <Route path="/achievements/:id" element={<AchievementDetailScreen />} />
        <Route path="/players/:pid/achievements" element={<AchievementsScreen />} />
        <Route path="/players/:pid/achievements/categories/:catId" element={<AchievementCategoryScreen />} />
        <Route path="/players/:pid/achievements/:id" element={<AchievementDetailScreen />} />
        <Route path="/schedule" element={<ScheduleScreen />} />
        <Route path="/tournaments/:id" element={<TournamentDetailScreen />} />
        <Route path="/tournaments/:id/players" element={<RegisteredPlayersScreen />} />
        <Route path="/tournaments/:id/results" element={<TournamentResultScreen />} />
        <Route path="/tournaments/:id/live" element={<LiveTournamentScreen />} />
        <Route path="/me/tournaments" element={<MyTournamentsScreen />} />
        <Route path="/players/:id" element={<PlayerProfileScreen />} />
        <Route path="/players/:id/tournaments" element={<MyTournamentsScreen />} />
        <Route path="/profile" element={<ProfileScreen />} />
        <Route path="/settings" element={<SettingsScreen />} />
        <Route path="/settings/edit" element={<EditProfileScreen />} />
        <Route path="*" element={<HomeScreen />} />
      </Routes>
      {!hideTabBar && <TabBar />}
      {/* Онбординг-сторис при первом открытии — поверх всего (флаг intro). */}
      <IntroGate enabled={auth.status === 'ready'} />
      {/* 6.2b «Straddle Pass+ активирован» — раньше прочих празднований. */}
      <PlusCelebration enabled={auth.status === 'ready'} />
      {/* 7.11 «Итоги сезона» — после всех прочих празднований. */}
      <SeasonRecap enabled={auth.status === 'ready'} />
      {/* C1 «момент получения»: показывается при непустой очереди празднований. */}
      <CelebrationSheet enabled={auth.status === 'ready'} />
      {/* RE2 «момент повышения» личного рейтинга (тише, ледяной). */}
      <RatingLevelUpSheet enabled={auth.status === 'ready'} />
      {/* Q3 рекап отбора — строго после листов выше (celebrationGate). */}
      <QualificationRecapSheet enabled={auth.status === 'ready'} />
      {/* Рекап пул-отбора «Калининград» — после рекапа Сочи (та же игра). */}
      <KaliningradRecap enabled={auth.status === 'ready'} />
    </div>
  );
}
